import React, { useState } from 'react';
import { useI18n } from '../context/I18nContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RotateCcw, User, Calendar, MapPin, AlertTriangle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReverseForm = () => {
  const { t } = useI18n();
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleDecode = async (e) => {
    e.preventDefault();
    
    if (!codiceFiscale.trim()) {
      setError(t('reverse.errors.required'));
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.get(`${API}/decode?cf=${encodeURIComponent(codiceFiscale.toUpperCase())}`);
      
      if (response.data.valid === false) {
        setError(response.data.error || t('reverse.errors.invalid'));
      } else {
        setResult(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('reverse.errors.invalid'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-5">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <RotateCcw className="h-6 w-6" />
          {t('reverse.title')}
        </h2>
        <p className="text-purple-100 text-sm mt-1">{t('reverse.subtitle')}</p>
      </div>

      <form onSubmit={handleDecode} className="p-6 space-y-5">
        <div>
          <Label htmlFor="cf-input" className="text-sm font-medium text-gray-700">
            {t('reverse.inputLabel')}
          </Label>
          <Input
            id="cf-input"
            value={codiceFiscale}
            onChange={(e) => setCodiceFiscale(e.target.value.toUpperCase())}
            placeholder={t('reverse.inputPlaceholder')}
            className="mt-1 font-mono text-lg tracking-wider uppercase"
            maxLength={16}
            data-testid="reverse-input"
          />
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-center gap-2" data-testid="reverse-error">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5 text-lg font-semibold"
          disabled={loading}
          data-testid="decode-btn"
        >
          {loading ? t('common.loading') : t('reverse.decode')}
        </Button>
      </form>

      {/* Result */}
      {result && (
        <div className="px-6 pb-6" data-testid="reverse-result">
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
            <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-purple-600" />
              {t('reverse.result.title')}
            </h3>
            
            <div className="space-y-3">
              {/* Gender */}
              <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                <User className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-xs text-gray-500">{t('reverse.result.gender')}</p>
                  <p className="font-medium">
                    {result.gender === 'M' ? t('calculator.male') : t('calculator.female')}
                  </p>
                </div>
              </div>

              {/* Birth Date */}
              <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-xs text-gray-500">{t('reverse.result.birthDate')}</p>
                  <p className="font-medium">{formatDate(result.birth_date)}</p>
                </div>
              </div>

              {/* Municipality Code */}
              <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                <MapPin className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-xs text-gray-500">{t('reverse.result.birthPlace')}</p>
                  <p className="font-medium font-mono">{result.comune_code}</p>
                </div>
              </div>

              {/* Omocodia */}
              {result.is_omocodia && (
                <div className="bg-amber-50 text-amber-800 p-3 rounded-lg text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {t('reverse.result.isOmocodia')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReverseForm;
